"use client";

import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/posthog-client";
import { motion, AnimatePresence, useReducedMotion } from "@/lib/motion";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Loader2,
  MapPin,
  Clock,
  Network as NetworkIcon,
  RotateCcw,
  Lock,
} from "@/components/icons";
import { ResultChip } from "./parts";
import type { Network } from "./parts";
import { cleanHost, validateDomain, countryToFlag, cn, type ProxyRoute } from "@/lib/utils";
import { GREEN, REGISTER_URL, YELLOW } from "@/lib/constants";

type Phase = "idle" | "loading" | "done";

import { MAX_TESTS, getTestCount, bumpTestCount, markRateLimited } from "./rate-limit";
import { TYPEWRITER_EXAMPLES, TICKER_FEED } from "./data";

const CURL_UNSUPPORTED_MESSAGE = "This website doesn't support curl";

export default function HeroVisual({
  defaultNetwork = "mobile",
  onComplete,
}: {
  defaultNetwork?: Network;
  onComplete?: () => void;
} = {}) {
  const prefersReducedMotion = useReducedMotion();
  const skip = !!prefersReducedMotion;

  const [network, setNetwork] = useState<Network>(defaultNetwork);
  const [url, setUrl] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [route, setRoute] = useState<ProxyRoute | null>(null);
  const [tickerIdx, setTickerIdx] = useState(0);
  const [inputFocused, setInputFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [ready, setReady] = useState(false);
  const [twText, setTwText] = useState("");
  const [twIdx, setTwIdx] = useState(0);
  const [twForward, setTwForward] = useState(true);
  const [twCursorOn, setTwCursorOn] = useState(true);
  const [animationsReady, setAnimationsReady] = useState(false);
  const [tabVisible, setTabVisible] = useState(true);

  const accent = network === "residential" ? YELLOW : GREEN;
  const hideGlow = !ready || phase === "done" || rateLimited;
  const canRun = ready && !rateLimited;
  const showPlaceholder = url === "" && !inputFocused;
  const idleAnimations = animationsReady && tabVisible && !skip;

  useEffect(() => {
    if (getTestCount() >= MAX_TESTS) {
      setRateLimited(true);
      onComplete?.();
    }
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ric = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }
    ).requestIdleCallback;
    let handle: number | ReturnType<typeof setTimeout>;
    if (typeof ric === "function") {
      handle = ric(() => setAnimationsReady(true), { timeout: 1500 });
    } else {
      handle = setTimeout(() => setAnimationsReady(true), 800);
    }
    const onVisibility = () => setTabVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      const cic = (window as unknown as { cancelIdleCallback?: (h: number) => void })
        .cancelIdleCallback;
      if (typeof cic === "function" && typeof handle === "number") cic(handle);
      else clearTimeout(handle as ReturnType<typeof setTimeout>);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!idleAnimations || !showPlaceholder) return;
    const fullText = TYPEWRITER_EXAMPLES[twIdx];
    const baseDelay = twForward ? 95 : 40;
    const t = setTimeout(() => {
      if (twForward) {
        if (twText.length < fullText.length) {
          setTwText(fullText.slice(0, twText.length + 1));
        } else {
          setTimeout(() => setTwForward(false), 1600);
        }
      } else {
        if (twText.length > 0) {
          setTwText(fullText.slice(0, twText.length - 1));
        } else {
          setTimeout(() => {
            setTwIdx((i) => (i + 1) % TYPEWRITER_EXAMPLES.length);
            setTwForward(true);
          }, 350);
        }
      }
    }, baseDelay);
    return () => clearTimeout(t);
  }, [twText, twForward, twIdx, showPlaceholder, idleAnimations]);

  useEffect(() => {
    if (!idleAnimations) return;
    const t = setInterval(() => setTwCursorOn((v) => !v), 530);
    return () => clearInterval(t);
  }, [idleAnimations]);

  useEffect(() => {
    if (!idleAnimations) return;
    const t = setInterval(() => setTickerIdx((i) => (i + 1) % TICKER_FEED.length), 3000);
    return () => clearInterval(t);
  }, [idleAnimations]);

  async function handleRun() {
    if (phase === "loading") return;
    if (rateLimited) {
      setPhase("idle");
      return;
    }
    const target = url.trim() === "" ? "proxidize.com" : url;
    if (url.trim() === "") setUrl("proxidize.com");
    const err = validateDomain(target);
    if (err) {
      setError(err);
      trackEvent("proxy_check_error", {
        domain: cleanHost(target),
        network,
        error: err,
        phase: "validation",
      });
      return;
    }
    setError(null);
    setPhase("loading");

    trackEvent("proxy_check_started", {
      domain: cleanHost(target),
      network,
    });

    try {
      const res = await fetch("/api/proxy-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: cleanHost(target), network }),
      });
      const data = (await res.json()) as {
        status: number;
        ip: string;
        country: string;
        city: string;
        latency_ms: number;
        network: string;
        error?: string;
      };

      if (res.status === 429) {
        setRateLimited(true);
        markRateLimited();
        setPhase("idle");
        trackEvent("proxy_check_rate_limited", { domain: cleanHost(target), network });
        onComplete?.();
        return;
      }

      if (!res.ok) throw new Error(data.error ?? "Proxy check failed");
      if (data.status === 403) throw new Error(CURL_UNSUPPORTED_MESSAGE);

      const newCount = bumpTestCount();
      if (newCount >= MAX_TESTS) {
        setRateLimited(true);
        onComplete?.();
      }

      trackEvent("proxy_check_run", {
        domain: cleanHost(target),
        network,
        status: data.status,
        country: data.country ?? null,
        city: data.city ?? null,
        latency_ms: data.latency_ms,
      });

      setRoute({
        flag: data.country ? countryToFlag(data.country) : "🌐",
        city: data.city ?? null,
        ip: data.ip ?? null,
        ms: data.latency_ms,
        status: data.status,
      });
      setPhase("done");
      onComplete?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Proxy check failed";
      setError(message);
      setPhase("idle");
      trackEvent("proxy_check_error", {
        domain: cleanHost(target),
        network,
        error: message,
        phase: "request",
      });
    }
  }

  function handleReset() {
    trackEvent("proxy_check_reset", { domain: cleanHost(url), network });
    setPhase("idle");
    setUrl("");
    setRoute(null);
    setError(null);
  }

  function handleNetworkSwitch(next: Network) {
    if (next !== network) {
      trackEvent("proxy_check_network_switched", { from: network, to: next });
    }
    setNetwork(next);
  }

  const tick = TICKER_FEED[tickerIdx];

  return (
    <div className={cn("relative shadow-2xl select-none", hideGlow && "tester-glow-done")}>
      <div className="relative z-1 overflow-hidden rounded-[calc(1rem-2px)] border border-white/[0.08] bg-[#111113]">
        <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-2.5">
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/70" />
              <div className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/70" />
              <div className="h-2.5 w-2.5 rounded-full bg-[#28c840]/70" />
            </div>
            <span className="ml-1 font-mono text-[11px] text-white/70">
              Test our proxies live on any site
            </span>
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-white/[0.05] bg-white/[0.04] p-0.5">
            {(["residential", "mobile"] as Network[]).map((n) => (
              <button
                key={n}
                onClick={() => handleNetworkSwitch(n)}
                className="cursor-pointer rounded-md px-2.5 py-1 text-[11px] font-medium transition-all"
                style={{
                  background: network === n ? "rgba(255,255,255,0.1)" : "transparent",
                  color: network === n ? "#fff" : "rgba(255,255,255,0.35)",
                }}
              >
                {n[0].toUpperCase() + n.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 py-3">
          <label
            htmlFor="proxy-domain-input"
            className="mb-2 block text-left font-mono text-[11px] text-white/70"
          >
            Enter a domain
          </label>
          <div className="flex gap-2">
            <motion.div
              className="relative min-w-0 flex-1"
              animate={
                error
                  ? { boxShadow: `0 0 0 1px #f8717180, 0 0 18px 2px #ef444466` }
                  : { boxShadow: "0 0 0 0 rgba(0,0,0,0)" }
              }
              transition={{ duration: 0.35, ease: "easeOut" }}
              style={{ borderRadius: 8 }}
            >
              <input
                type="text"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (error) setError(null);
                  if (phase === "done") setPhase("idle");
                }}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                onKeyDown={(e) => e.key === "Enter" && handleRun()}
                disabled={rateLimited}
                placeholder=""
                id="proxy-domain-input"
                aria-describedby="proxy-input-hint"
                aria-invalid={!!error}
                className="h-9 w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 font-mono text-sm text-white/85 transition-colors outline-none focus:border-white/20 disabled:opacity-40"
              />
              {showPlaceholder && !rateLimited && (
                <span
                  id="proxy-input-hint"
                  aria-hidden
                  className="pointer-events-none absolute inset-0 flex items-center px-3 font-mono text-sm text-white/30 select-none"
                >
                  {twText}
                  <span
                    aria-hidden
                    style={{
                      display: "inline-block",
                      marginLeft: 1,
                      width: 1.5,
                      height: 14,
                      backgroundColor: "rgba(255,255,255,0.35)",
                      opacity: twCursorOn ? 1 : 0,
                      transition: "opacity 0.1s",
                      verticalAlign: "middle",
                    }}
                  />
                </span>
              )}
            </motion.div>

            {phase === "done" ? (
              <button
                onClick={handleReset}
                aria-label="Reset proxy check"
                className="flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-white/[0.08] px-3 text-xs font-medium text-[#a0a0a0] transition-colors hover:border-white/20"
              >
                <RotateCcw size={12} aria-hidden /> Reset
              </button>
            ) : (
              <button
                onClick={handleRun}
                disabled={!canRun || phase === "loading"}
                aria-label="Run proxy check"
                className="flex h-9 cursor-pointer items-center gap-1.5 rounded-lg px-4 text-sm font-bold text-black transition-opacity disabled:opacity-30"
                style={{ background: accent }}
              >
                {phase === "loading" ? (
                  <Loader2 size={14} className="animate-spin" aria-hidden />
                ) : (
                  <ArrowRight size={14} aria-hidden />
                )}
                {phase === "loading" ? "Routing" : "Run"}
              </button>
            )}
          </div>
        </div>

        <div className="flex h-12 items-center border-t border-white/[0.05] px-4">
          <AnimatePresence mode="wait" initial={false}>
            {phase === "done" && route ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="flex flex-wrap items-center gap-1.5"
              >
                <ResultChip
                  icon={
                    route.status && route.status >= 400 ? (
                      <AlertCircle size={11} aria-hidden />
                    ) : (
                      <CheckCircle2 size={11} aria-hidden />
                    )
                  }
                  label={
                    !route.status || route.status === 0
                      ? "Connected"
                      : route.status >= 200 && route.status < 300
                        ? `${route.status} OK`
                        : route.status >= 300 && route.status < 400
                          ? `${route.status} Redirect`
                          : `${route.status} Error`
                  }
                  color={
                    !route.status ||
                    route.status === 0 ||
                    (route.status >= 200 && route.status < 300)
                      ? accent
                      : route.status >= 300 && route.status < 400
                        ? "#facc15"
                        : "#fb923c"
                  }
                  tooltip="Site's response"
                />
                {route.city && (
                  <ResultChip
                    icon={
                      <span aria-hidden className="text-[12px] leading-none">
                        {route.flag}
                      </span>
                    }
                    label={route.city}
                    tooltip="Proxy location"
                  />
                )}
                {route.ip && (
                  <ResultChip
                    icon={<MapPin size={11} aria-hidden />}
                    label={route.ip}
                    mono
                    tooltip="Proxy IP"
                  />
                )}
                <ResultChip
                  icon={<Clock size={11} aria-hidden />}
                  label={`${route.ms}ms`}
                  color={route.ms < 1500 ? "#4ade80" : route.ms < 3000 ? "#facc15" : "#fb923c"}
                  mono
                  tooltip="Response time"
                />
                <ResultChip
                  icon={<NetworkIcon size={11} aria-hidden />}
                  label={network === "residential" ? "Residential" : "Mobile"}
                  tooltip="Network type"
                />
              </motion.div>
            ) : phase === "loading" ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 font-mono text-[11px]"
                style={{ color: accent }}
              >
                <span className="flex gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <motion.span
                      key={i}
                      className="h-1 w-1 rounded-full"
                      style={{ background: accent }}
                      animate={{ opacity: [0.2, 1, 0.2] }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        delay: i * 0.1,
                        ease: "easeInOut",
                      }}
                    />
                  ))}
                </span>
                Routing through {network} proxy · resolving {cleanHost(url)}
              </motion.div>
            ) : rateLimited ? (
              <motion.div
                key="limited"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-1.5 font-mono text-[11px] text-[#a0a0a0]"
              >
                <Lock size={11} aria-hidden className="text-white/30" />3 free checks used ·{" "}
                <a href={REGISTER_URL} className="text-white hover:underline">
                  Sign up
                </a>{" "}
                for unlimited testing
              </motion.div>
            ) : error ? (
              <motion.div
                key="error"
                role="alert"
                initial={{ opacity: 0, x: -2 }}
                animate={{ opacity: 1, x: [0, -3, 3, -2, 2, 0] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="flex items-center gap-1.5 font-mono text-[11px]"
                style={{ color: "#fca5a5" }}
              >
                <AlertCircle size={11} aria-hidden style={{ color: "#f87171" }} />
                {error}
              </motion.div>
            ) : (
              <motion.div
                key="idle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 font-mono text-[11px] text-[#a0a0a0]"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-white/50" />
                Press Run to test
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
